import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CategoryParams,
  CursorPaginatedResponse,
  ProductCategory,
  ProductCategoryData,
  ServiceCategory,
  ServiceCategoryData,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { categoryApiMock } from '@/libs/mocks/handlers/category'

const categoryApiReal = {
  createService: async (body: Partial<ServiceCategory>): Promise<Partial<ServiceCategory>> => {
    return await api
      .post<
        ApiResponse<Partial<ServiceCategory>>
      >(URLs.Category.createService(body.orgID || ''), body)
      .then((response) => response.data.data)
  },
  createProduct: async (body: Partial<ProductCategory>): Promise<Partial<ProductCategory>> => {
    return await api
      .post<
        ApiResponse<Partial<ProductCategory>>
      >(URLs.Category.createProduct(body.orgID || ''), body)
      .then((response) => response.data.data)
  },
  getByIDService: async (params: Partial<ServiceCategory>): Promise<Partial<ServiceCategory>> => {
    const { orgID, serviceCategoryID } = params
    return await api
      .get<
        ApiResponse<Partial<ServiceCategory>>
      >(URLs.Category.byIDService(orgID || '', serviceCategoryID || ''))
      .then((response) => response.data.data)
  },
  getByIDProduct: async (params: Partial<ProductCategory>): Promise<Partial<ProductCategory>> => {
    const { orgID, productCategoryID } = params
    return await api
      .get<
        ApiResponse<Partial<ProductCategory>>
      >(URLs.Category.byIDProduct(orgID || '', productCategoryID || ''))
      .then((response) => response.data.data)
  },
  list: async (params: CategoryParams): Promise<ServiceCategory[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<ServiceCategoryData>>(URLs.Category.listService(orgID, query))
      .then((response) => response.data.data?.serviceCategories)
  },
  listCursorProduct: async (
    params: CategoryParams,
  ): Promise<CursorPaginatedResponse<ProductCategory>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<ProductCategoryData>>(URLs.Category.listProduct(orgID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.productCategories,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  listCursorService: async (
    params: CategoryParams,
  ): Promise<CursorPaginatedResponse<ServiceCategory>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<ServiceCategoryData>>(URLs.Category.listService(orgID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.serviceCategories,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  updateService: async (payload: Partial<ServiceCategory>) => {
    return await api
      .patch(
        URLs.Category.byIDService(payload?.orgID || '', payload?.serviceCategoryID || ''),
        payload,
      )
      .then((response) => response.data)
  },
  updateProduct: async (payload: Partial<ProductCategory>) => {
    return await api
      .patch(
        URLs.Category.byIDProduct(payload?.orgID || '', payload?.productCategoryID || ''),
        payload,
      )
      .then((response) => response.data)
  },
  deleteService: async (payload: Partial<ServiceCategory>) => {
    const { orgID, serviceCategoryID } = payload
    return await api
      .delete(URLs.Category.byIDService(orgID || '', serviceCategoryID || ''))
      .then((response) => response.data)
  },
  deleteProduct: async (payload: Partial<ProductCategory>) => {
    const { orgID, productCategoryID } = payload
    return await api
      .delete(URLs.Category.byIDProduct(orgID || '', productCategoryID || ''))
      .then((response) => response.data)
  },
}

export const categoryApi: typeof categoryApiReal = IS_MOCK_MODE ? categoryApiMock : categoryApiReal
